import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { UnstyledButton } from 'components';
import letterIcon from 'images/letter.svg';
import logoutIcon from 'images/logout.svg';
import boxIcon from 'images/box.svg';
import { ChevronDown } from 'lucide-react';
import { UserProfile } from 'context/auth/context';

const StyledProfileElement = styled.div`
  margin-left: auto;
  display: inline-block;
  position: relative;
`;
const StyledProfileButton = styled(UnstyledButton)<{ isOpen: boolean }>`
  outline: none;
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: ${p => (p.isOpen ? '8px 8px 0 0' : '8px')};
  color: ${p => (p.isOpen ? p.theme.colors.black : p.theme.colors.navFg)};
  background-color: ${p => (p.isOpen ? p.theme.colors.white : 'transparent')};
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 2px 2px rgba(255, 255, 255, 0.4);
  }
`;
const StyledProfilePicture = styled.img`
  margin-left: ${p => p.theme.spacing.xs};
  border-radius: 8px;
  height: 32px;
`;
const StyledButtonCaret = styled.span`
  margin-left: ${p => p.theme.spacing.xs};
  display: flex;
  align-items: center;
`;
const StyledProfileName = styled.div`
  margin-bottom: 4px;
`;
const StyledProfileMenu = styled.div<{ isOpen: boolean }>`
  display: ${p => (p.isOpen ? 'block' : 'none')};
  position: absolute;
  z-index: 20;
  right: 0;
  border-radius: 8px 0 8px 8px;
  background-color: ${p => p.theme.colors.white};
  min-width: 250px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 8px 15px rgba(0, 0, 0, 0.08);
`;
const StyledProfileMenuItem = styled.div`
  padding: 14px;
  border-bottom: 1px solid ${p => p.theme.colors.midGrey};

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 8px 8px;
  }
`;
const StyledProfileMenuItemClickable = styled(UnstyledButton)<{
  borderTop?: boolean;
}>`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 14px;
  width: 100%;
  border-bottom: 1px solid ${p => p.theme.colors.midGrey};
  font-size: ${p => p.theme.fontSize.sm};

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 8px 8px;
  }

  ${props =>
    props.borderTop &&
    `
    border-top: 1px solid ${props.theme.colors.midGrey};
  `}

  &:hover {
    background-color: ${p => p.theme.colors.lightGrey};
    text-decoration: underline;
  }
`;
const StyledProfileMenuItemLink = styled(Link)`
  font-size: ${p => p.theme.fontSize.sm};
  align-items: center;
  cursor: pointer;
  margin: 10px 0px 10px 10px;
  display: inline-flex;
  color: ${p => p.theme.colors.black};

  &.active {
    font-weight: bold;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 8px 8px;
  }
`;
const StyledProfileMenuItemSmallContent = styled.div`
  font-size: ${p => p.theme.fontSize.xs};
  color: ${p => p.theme.colors.midGrey};
`;
const StyledProfileMenuSecurityContent = styled.div`
  font-size: ${p => p.theme.fontSize.xs};
  font-family: 'Open Sans', sans-serif;
  color: ${p => p.theme.colors.black};
  margin-top: 4px;
`;
const StyledProfileMenuSecurityLink = styled.a`
  color: ${p => p.theme.colors.deepBlue};
`;
const StyledMenuImage = styled.img`
  width: 20px;
  margin-right: 7px;
  filter: ${p => p.theme.colors.iconFilter};
`;
const StyledSandboxesContainer = styled.div`
  margin: 14px;
`;
const StyledSandboxesLabel = styled.p`
  font-size: ${p => p.theme.fontSize.sm};
  margin-bottom: 14px;
  font-weight: bold;
`;

interface Props {
  profile: UserProfile;
  sandboxes: Sandbox[];
  onSignOut: () => void;
  onReportAnError: () => void;
  currentSandbox?: string;
  securityExamValidUntil: Date;
  securityExamRequired: boolean;
  trainingSiteURL: string;
}

export const ProfileMenu = (props: Props) => {
  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
    if (ref.current) {
      if (
        event.target instanceof Element &&
        ref.current.contains(event.target)
      ) {
        return;
      }
    }
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      ref.current?.focus();
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const menu = menuRef.current;
      if (!menu) return;
      const focusable = Array.from(
        menu.querySelectorAll<HTMLElement>('a, button')
      ).filter(el => !el.hasAttribute('disabled'));
      const current = document.activeElement as HTMLElement;
      const idx = focusable.indexOf(current);
      if (event.key === 'ArrowDown') {
        focusable[idx + 1 < focusable.length ? idx + 1 : 0]?.focus();
      } else {
        focusable[idx - 1 >= 0 ? idx - 1 : focusable.length - 1]?.focus();
      }
    }
  };

  useEffect(() => {
    // install global event listeners
    document.addEventListener('click', handleDocumentClick, false);
    document.addEventListener('touchend', handleDocumentClick, false);
    return () => {
      // uninstall global event listeners
      document.removeEventListener('click', handleDocumentClick, false);
      document.removeEventListener('touchend', handleDocumentClick, false);
    };
  }, []);

  const renderSecurityExamInfo = () => {
    if (!props.securityExamRequired) {
      return null;
    }
    const securityExamTime = `${props.securityExamValidUntil.getDate()}.${
      props.securityExamValidUntil.getMonth() + 1
    }.${props.securityExamValidUntil.getFullYear()}`;
    return (
      <StyledProfileMenuSecurityContent>
        Retake&nbsp;
        <StyledProfileMenuSecurityLink href={props.trainingSiteURL}>
          security training
        </StyledProfileMenuSecurityLink>
        &nbsp;by {securityExamTime}
      </StyledProfileMenuSecurityContent>
    );
  };

  const { sandboxes, profile, onSignOut, onReportAnError, currentSandbox } =
    props;
  return (
    <StyledProfileElement id="nav-profile-menu" onKeyDown={handleKeyDown}>
      <StyledProfileButton
        ref={ref}
        isOpen={open}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
      >
        <StyledProfilePicture src={profile.picture} />
        <StyledButtonCaret>
          <ChevronDown size={12} aria-hidden={true} />
        </StyledButtonCaret>
      </StyledProfileButton>
      <StyledProfileMenu isOpen={open} ref={menuRef} role="menu">
        <StyledProfileMenuItem>
          <StyledProfileName>{profile.name}</StyledProfileName>
          <StyledProfileMenuItemSmallContent>
            <div>{profile.email}</div>
          </StyledProfileMenuItemSmallContent>
          {renderSecurityExamInfo()}
        </StyledProfileMenuItem>
        <StyledSandboxesContainer>
          <StyledSandboxesLabel>Your Sandboxes:</StyledSandboxesLabel>
          {sandboxes.map(sandbox => (
            <StyledProfileMenuItemLink
              className={currentSandbox === sandbox.id ? 'active' : ''}
              key={sandbox.id}
              to={`/${sandbox.id}`}
            >
              <StyledMenuImage src={boxIcon} alt={sandbox.name} />
              {sandbox.name}
            </StyledProfileMenuItemLink>
          ))}
        </StyledSandboxesContainer>
        <StyledProfileMenuItemClickable borderTop onClick={onReportAnError}>
          <StyledMenuImage src={letterIcon} alt="Report an error" />
          Report an error
        </StyledProfileMenuItemClickable>
        <StyledProfileMenuItemClickable onClick={onSignOut}>
          <StyledMenuImage src={logoutIcon} alt="Sign out" />
          Sign out
        </StyledProfileMenuItemClickable>
      </StyledProfileMenu>
    </StyledProfileElement>
  );
};
